import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { API_CONFIG } from '@/config/envConfig';

export const useCreateGroupByExcel = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const createGroup = async (groupName: string, file: File | null): Promise<boolean> => {
    if (!user?.id || !token) {
      toast({
        title: "Authentication Error",
        description: "You must be logged in to create a patient group.",
        variant: "destructive",
      });
      return false;
    }

    if (!groupName.trim()) {
      toast({
        title: "Validation Error",
        description: "Please provide a name for the patient group.",
        variant: "destructive",
      });
      return false;
    }

    // Validate file if provided
    if (file) {
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (!validTypes.includes(file.type) && !file.name.match(/\.(xls|xlsx)$/i)) {
        toast({
          title: "Invalid File Type",
          description: "Please upload a valid Excel file (.xls or .xlsx).",
          variant: "destructive",
        });
        return false;
      }
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('groupName', groupName.trim());
      
      if (file) {
        formData.append('file', file);
      }

      const endpoint = `${API_CONFIG.API_BASE}/api/ngpep/${user.id}/patient-group/create-with-excel`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      toast({
        title: "Patient Group Created",
        description: `Successfully created patient group "${groupName}".`,
      });

      return true;
    } catch (error: any) {
      console.error('Error creating patient group:', error);
      
      let errorMessage = "Failed to create patient group.";
      
      if (error.message.includes('Invalid file format')) {
        errorMessage = "Invalid Excel file format. Please check your file and try again.";
      } else if (error.message.includes('duplicate')) {
        errorMessage = "A patient group with this name already exists.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });

      return false;
    } finally {
      setLoading(false);
    }
  };

  return { createGroup, loading };
};
